import { FC, useState } from 'react';
import { useAuthContext } from '@/context/AuthContext.tsx';

type FaqItem = {
  id: string;
  title: string;
  content: string;
};

const clientFaqData: FaqItem[] = [
  {
    id: 'client-what-is',
    title: '1. What is CareTailor Ltd?',
    content:
      'We help you find and connect with independent carers. We do not provide care or employ carers.',
  },
  {
    id: 'client-how-it-works',
    title: '2. How does it work?',
    content:
      'Create an account → view carers → contact carers → arrange care directly.',
  },
  {
    id: 'client-vetted',
    title: '3. Are carers vetted?',
    content:
      'We check Enhanced DBS and insurance. You must verify qualifications, experience, and references.',
  },
  {
    id: 'client-rates',
    title: '4. How are rates set?',
    content:
      'Carers set rates based on experience and qualifications. This is typically 50–60% cheaper than traditional home care companies.',
  },
  {
    id: 'client-provide-care',
    title: '5. Does CareTailor provide care?',
    content:
      'No. All care arrangements are between you and the carer.',
  },
  {
    id: 'client-cancel',
    title: '6. What if I need to cancel?',
    content:
      'Cancellations must be agreed directly with the carer. We recommend putting agreements in writing.',
  },
  {
    id: 'client-insurance',
    title: '7. Insurance & Safety',
    content:
      'Carers provide their own insurance. Clients should ensure arrangements are safe and may need to review their own insurance.',
  },
  {
    id: 'client-reviews',
    title: '8. Reviews',
    content:
      'You can leave reviews to help other clients make informed choices.',
  },
  {
    id: 'client-account',
    title: '9. Account management',
    content:
      'You must be 18+. Accounts can be deactivated by you or CareTailor Ltd for safety or term breaches.',
  },
];

const carerFaqData: FaqItem[] = [
  {
    id: 'carer-who-can-join',
    title: '1. Who can join?',
    content:
      '18+, legal right to work in the UK, valid insurance, up-to-date DBS.',
  },
  {
    id: 'carer-employment',
    title: '2. Employment status',
    content:
      'You are self-employed. CareTailor Ltd only connects you with clients.',
  },
  {
    id: 'carer-rates',
    title: '3. Setting rates',
    content:
      'Set your own rates depending on your qualifications and experience. Clients typically pay 50–60% less than through traditional agencies.',
  },
  {
    id: 'carer-checks',
    title: '4. Platform checks',
    content:
      'We check DBS and insurance. You are responsible for all other information you provide.',
  },
  {
    id: 'carer-cancellations',
    title: '5. Cancellations & bookings',
    content:
      'Set your own cancellation policy. Only accept work you can safely provide. Put agreements in writing.',
  },
  {
    id: 'carer-safety',
    title: '6. Safety & conduct',
    content:
      'CareTailor Ltd may remove carers for safety, compliance, or conduct concerns.',
  },
  {
    id: 'carer-leaving',
    title: '7. Leaving the platform',
    content:
      'You can deactivate your profile anytime. CareTailor Ltd may also remove you if necessary.',
  },
  {
    id: 'carer-support',
    title: '8. Support',
    content:
      'Contact support@caretailor.co.uk for questions or concerns.',
  },
];

const FaqPage: FC = () => {
  const { currentUser } = useAuthContext();
  const isBusinessProvider = currentUser?.role === 'business_provider';

  const faqData = isBusinessProvider ? carerFaqData : clientFaqData;
  const heading = isBusinessProvider ? 'Carer FAQs' : 'Client FAQs';

  const [openIds, setOpenIds] = useState<Set<string>>(new Set([faqData[0]?.id]));

  const toggleAccordion = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-6 w-full mx-auto">
      <h1 className="text-2xl font-semibold">{heading}</h1>

      <div className="flex flex-col gap-4">
        {faqData.map((item) => {
          const isOpen = openIds.has(item.id);

          return (
            <div
              key={item.id}
              className="bg-[#F6F6F4] rounded-xl overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleAccordion(item.id)}
                className="w-full flex items-center justify-between p-4 text-left font-semibold text-base cursor-pointer"
              >
                <span>{item.title}</span>
                <img
                  src="/assets/arrowDown.svg"
                  alt=""
                  className={`w-4 h-4 transition-transform duration-500 ease-in-out ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
              <div
                className={`grid transition-all duration-500 ease-in-out ${
                  isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <div className="overflow-hidden">
                  <div className="px-4 pb-4 text-sm text-gray-700 leading-relaxed">
                    {item.content}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FaqPage;
